import backArrow from '../../../../../assets/img/backArrow.png';
import { useState, useEffect, useContext } from 'react';
import { GlobalContext } from 'GlobalContext';
import { USDollar } from 'utils/format';
import api from 'api';
import { get } from 'utils/fetch';
import { message, Spin, Input, Button, Upload, InputNumber } from 'antd';
import { ActionState } from 'plaid/dist/api';
import {
    EditOutlined,
    UploadOutlined,
    DeleteOutlined,
    ConsoleSqlOutlined,
} from '@ant-design/icons';
import { getMimeType, stringifyDict, isPhoneSize } from 'utils/util';
import { Files } from 'pages/RecordInsertion/InvestorInsertionTabs/Files';

export function ConstructionDetailed() {
    const { manageAssetId, accessToken, assetManager, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [documentTypeDict, setDocumentTypeDict] = useState({});
    const { constructionId, setConstructionId } = assetManager;

    // table values
    const [startDate, setStartDate] = useState(null);
    const [targetFinishDate, setTargetFinishDate] = useState(null);
    const [revisedFinishedDate, setRevisedFinishedDate] = useState(null);
    const [actualFinishDate, setActualFinishDate] = useState(null);
    const [budget, setBudget] = useState(0);
    const [permitPackage, setPermitPackage] = useState([]);
    const [newPermitFiles, setNewPermitFiles] = useState([]);

    const [construction, setConstruction] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(
        constructionId === -1 ? false : true
    );
    const [
        constructionTargetCompletionDate,
        setConstructionTargetCompletionDate,
    ] = useState(null);

    // deletion variables
    const [fileNameToDelete, setFileNameToDelete] = useState('');

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchConstructionInfo(constructionId) {
        await get(
            api.asset.getConstruction(constructionId),
            setConstruction,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        setLoading(false);
    }

    function restore() {
        setStartDate(construction?.start_date);
        setTargetFinishDate(construction?.target_finish_date);
        setRevisedFinishedDate(construction?.revised_finished_date);
        setActualFinishDate(construction?.actual_finish_date);
        setBudget(construction?.budget);
        setPermitPackage(construction?.permit_package);
        setConstructionTargetCompletionDate(
            construction?.construction_target_completion_date
        );
    }

    useEffect(() => {
        if (construction) {
            restore();
        }
    }, [construction]);

    useEffect(() => {
        if (constructionId <= 0) {
            return;
        }
        fetchConstructionInfo(constructionId);
    }, [constructionId]);

    useEffect(() => {
        if (constructionId === -1) {
            setEditMode(true); // auto-enable editing when adding
        } else {
            setEditMode(false); // disable editing until user clicks "Edit"
        }
    }, [constructionId]);

    function unchanged() {
        if (constructionId === -1) {
            return false;
        }
        return (
            startDate === construction.start_date &&
            targetFinishDate === construction.target_finish_date &&
            revisedFinishedDate === construction.revised_finished_date &&
            actualFinishDate === construction.actual_finish_date &&
            budget === construction.budget &&
            permitPackage === construction.permit_package &&
            constructionTargetCompletionDate ===
                construction.construction_target_completion_date
        );
    }

    async function upsert() {
        if (unchanged()) return;
        setLoading(true);
        const body = JSON.stringify({
            manageAssetId,
            startDate,
            targetFinishDate,
            revisedFinishedDate,
            actualFinishDate,
            budget,
            permitPackage,
            constructionTargetCompletionDate,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        const response = await fetch(
            api.asset.upsertConstruction(constructionId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();

        if (idRes === 0) {
            messageApi.error({
                type: 'error', // or 'error', 'info', 'warning', 'loading'
                content:
                    constructionId === -1
                        ? 'Construction insertion failed'
                        : 'Construction update failed',
                duration: 3, // seconds (optional)
            });
            setConstructionId(0);
        }
        // console.log(constructionId);

        if (constructionId === -1) {
            setConstructionId(idRes);
            await uploadPermitFiles(idRes);
        } else {
            await uploadPermitFiles(constructionId);
        }

        messageApi.success({
            content:
                constructionId === -1
                    ? 'Construction Created'
                    : 'Upload success',
            duration: 3,
        });
        fetchConstructionInfo(idRes);
        setEditMode(false);
        setLoading(false);
    }

    async function deleteConstruction() {
        setLoading(true);

        const options = {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        };

        const response = await fetch(
            api.asset.deleteConstruction(constructionId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();

        if (idRes === 0) {
            messageApi.error({
                type: 'error',
                content: 'Construction deletion failed',
                duration: 3,
            });
        } else {
            messageApi.open({
                type: 'success',
                content: 'Construction deletion success',
                duration: 3,
            });
            setConstructionId(0);
        }
    }

    async function fetchPermitFiles() {
        setLoading(true);
        await get(
            api.asset.fetchConstructionFiles(constructionId),
            setPermitPackage,
            (errors) => errors.forEach((err) => messageApi.error(err.message)),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (constructionId <= 0) {
            return;
        }
        fetchPermitFiles();
    }, [constructionId]);

    const handlePermitUpload = (e) => {
        const newFiles = [];
        for (let i = 0; i < e.target.files.length; i++) {
            const file = e.target.files[i];
            if (file) {
                newFiles.push({
                    filename: file.name,
                    url: URL.createObjectURL(file),
                    blob: file,
                });
            }
        }
        setNewPermitFiles([...newPermitFiles, ...newFiles]);
    };

    async function uploadPermitFiles(realId) {
        if (newPermitFiles.length === 0) {
            return;
        }

        const formData = new FormData();
        formData.append('constructionId', realId);

        newPermitFiles.forEach((file) => {
            formData.append(
                'files',
                new File([file.blob], file.filename, {
                    type: getMimeType(file.filename),
                })
            );
        });

        await fetch(api.file.addConstructionFiles, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        });
        refreshSession();
        setNewPermitFiles([]);
        await fetchPermitFiles();
    }

    async function deletePermitFiles(filename) {
        const options = {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                constructionId,
                fileNameToDelete: filename,
            }),
        };
        const result = await fetch(api.file.deleteConstructionFiles, options);
        refreshSession();
        setLoading(true);
        fetchPermitFiles();
    }

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    return (
        <>
            {contextHolder}
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            marginLeft: '20px',
                            position: 'fixed',
                            background: 'white',
                            width: '100%',
                            zIndex: 1,
                            height: '40px',
                            alignItems: 'end',
                        }}
                    >
                        <img
                            style={{
                                width: '15px',
                                height: '20px',
                                cursor: 'pointer',
                            }}
                            src={backArrow}
                            onClick={() => {
                                setConstructionId(0);
                            }}
                        ></img>
                        {constructionId !== -1 && (
                            <div
                                style={{
                                    width: 'fit-content',
                                    color: 'blue',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    marginLeft: '20px',
                                }}
                                onClick={() => {
                                    if (editMode) {
                                        restore();
                                    }
                                    setEditMode((prev) => !prev);
                                }}
                            >
                                {editMode ? 'Restore' : 'Edit'}
                            </div>
                        )}
                    </div>

                    <div style={{ fontSize: '18px' }}>
                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : {
                                          ...entryStyle,
                                          display: 'flex',
                                          marginTop: '60px',
                                      }
                            }
                        >
                            <div>Start Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={startDate || ''}
                                    onChange={(e) =>
                                        setStartDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{startDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Target Completion Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={
                                        constructionTargetCompletionDate || ''
                                    }
                                    onChange={(e) =>
                                        setConstructionTargetCompletionDate(
                                            e.target.value
                                        )
                                    }
                                ></input>
                            ) : (
                                <div>{constructionTargetCompletionDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Target Finish Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={targetFinishDate || ''}
                                    onChange={(e) =>
                                        setTargetFinishDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{targetFinishDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Revised Finished Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={revisedFinishedDate || ''}
                                    onChange={(e) =>
                                        setRevisedFinishedDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{revisedFinishedDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Actual Finish Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={actualFinishDate || ''}
                                    onChange={(e) =>
                                        setActualFinishDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{actualFinishDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Budget: </div>
                            {editMode ? (
                                <InputNumber
                                    value={budget}
                                    onChange={setBudget}
                                    controls={false} // removes the up/down arrows
                                    placeholder="Budget"
                                    style={{
                                        border: 'none',
                                        borderBottom: '1px solid black',
                                        outline: 'none',
                                        width: '120px',
                                        borderRadius: 0,
                                    }}
                                    onFocus={(e) =>
                                        (e.target.style.outline = 'none')
                                    }
                                    onBlur={(e) =>
                                        (e.target.style.outline = 'none')
                                    }
                                />
                            ) : (
                                <div>{USDollar.format(budget)}</div>
                            )}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                fontSize: '20px',
                                marginLeft: '20px',
                            }}
                        >
                            <div>Permit Package:</div>

                            {[...(permitPackage ?? []), ...newPermitFiles].map(
                                (file, idx) => {
                                    const url =
                                        typeof file === 'string'
                                            ? file
                                            : file.url;
                                    const filename =
                                        typeof file === 'string'
                                            ? file.split('/').pop()
                                            : file.filename ||
                                              file.url.split('/').pop();

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {filename}
                                            </a>

                                            {editMode && (
                                                <Button
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => {
                                                        if (
                                                            window.confirm(
                                                                'Are you sure you want to delete this file?'
                                                            )
                                                        ) {
                                                            if (
                                                                idx <
                                                                permitPackage.length
                                                            ) {
                                                                // Existing file — call deletePermitFiles with needed info
                                                                const fileUrl =
                                                                    typeof permitPackage[
                                                                        idx
                                                                    ] ===
                                                                    'string'
                                                                        ? permitPackage[
                                                                              idx
                                                                          ]
                                                                        : permitPackage[
                                                                              idx
                                                                          ].url;

                                                                const filename =
                                                                    fileUrl
                                                                        .split(
                                                                            '/'
                                                                        )
                                                                        .pop();

                                                                deletePermitFiles(
                                                                    filename
                                                                )
                                                                    .then(
                                                                        () => {
                                                                            // success handler
                                                                            const updated =
                                                                                [
                                                                                    ...permitPackage,
                                                                                ];
                                                                            updated.splice(
                                                                                idx,
                                                                                1
                                                                            );
                                                                            setPermitPackage(
                                                                                updated
                                                                            );
                                                                            messageApi.success(
                                                                                'File deleted successfully.'
                                                                            );
                                                                        }
                                                                    )
                                                                    .catch(
                                                                        () => {
                                                                            // error handler
                                                                            messageApi.error(
                                                                                'Failed to delete file.'
                                                                            );
                                                                        }
                                                                    );
                                                            } else {
                                                                // New unsaved file — just remove from state
                                                                const updatedNew =
                                                                    [
                                                                        ...newPermitFiles,
                                                                    ];
                                                                updatedNew.splice(
                                                                    idx -
                                                                        permitPackage.length,
                                                                    1
                                                                );
                                                                setNewPermitFiles(
                                                                    updatedNew
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                    );
                                }
                            )}

                            {editMode && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginTop: '10px',
                                    }}
                                >
                                    <input
                                        id="permitUploadInput"
                                        type="file"
                                        multiple
                                        accept=".pdf"
                                        onChange={handlePermitUpload}
                                        style={{ display: 'none' }}
                                    />

                                    <Button
                                        icon={<UploadOutlined />}
                                        onClick={() =>
                                            document
                                                .getElementById(
                                                    'permitUploadInput'
                                                )
                                                .click()
                                        }
                                    >
                                        Upload PDF
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        style={{
                            height: '30px',
                            width: '60px',
                            borderRadius: '30px',
                            background: 'rgb(17 32 86)',
                            color: 'white',
                            cursor: 'pointer',
                            marginLeft: '20px',
                        }}
                        onClick={upsert}
                    >
                        Save
                    </button>
                    <button
                        style={{
                            height: '30px',
                            width: '60px',
                            borderRadius: '30px',
                            background: 'rgb(86 17 17)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (
                                window.confirm(
                                    'Are you sure you want to delete this construction?'
                                )
                            ) {
                                deleteConstruction();
                            }
                        }}
                    >
                        Delete
                    </button>
                </>
            )}
        </>
    );
}
